import { Box, Text } from '@mantine/core';
import { Controls, Handle, ReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Fragment, memo, useMemo } from 'react';
import FlowStepIcon from '../Flow/FlowStepIcon';
import { formatStatValue } from './ChannelBreakdownTable';

const nodeLabel = (data) => data.custom?.labels?.id || data.label;

const handleStyle = { width: '10px', height: '10px' };

const StatsBody = ({ step, currency }) => {
  if (!step) return null;
  if (step.error) {
    return (
      <Text size="xs" c="red" mt={4}>
        Failed to load stats
      </Text>
    );
  }
  if (step.unsupported) {
    return (
      <Text size="xs" c="dimmed" mt={4}>
        No per-step stats for ad channels
      </Text>
    );
  }
  const columns = step.config?.columns || [];
  return (
    <Box
      mt={6}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        gap: '1px 12px',
        justifyContent: 'center',
        borderTop: '1px solid var(--mantine-color-gray-3)',
        paddingTop: 6,
      }}
    >
      {columns.map((col) => (
        <Fragment key={`${col.label}|${col.statKey}`}>
          <Text size="xs" c="dimmed" ta="left">
            {col.label}
          </Text>
          <Text size="xs" fw={600} ta="right">
            {formatStatValue(col, step.statsByAction?.[col.stat], step.currency ?? currency)}
          </Text>
        </Fragment>
      ))}
    </Box>
  );
};

const StatDefaultNode = memo(({ data }) => (
  <>
    <Handle type="target" position="top" isConnectable={false} style={{ top: '-5px', ...handleStyle }} />
    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      <FlowStepIcon icon={data.custom?.key} />
      <Text size="sm" component="span">
        {nodeLabel(data)}
      </Text>
    </Box>
    <StatsBody step={data.step} currency={data.currency} />
    <Handle type="source" position="bottom" isConnectable={false} style={{ bottom: '-5px', ...handleStyle }} />
  </>
));

const StatBranchNode = memo(({ data }) => (
  <>
    <Handle type="target" position="top" isConnectable={false} style={{ top: '-5px', ...handleStyle }} />
    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      <FlowStepIcon icon={data.custom?.key} />
      <Text size="sm" component="span">
        {nodeLabel(data)}
      </Text>
    </Box>
    <Handle
      type="source"
      position="bottom"
      id="conditionMatched"
      isConnectable={false}
      style={{ left: '25px', top: 'auto', background: 'var(--mantine-color-green-6)', bottom: '-5px', ...handleStyle }}
    />
    <Handle
      type="source"
      position="bottom"
      id="conditionNotMatched"
      isConnectable={false}
      style={{
        left: 'auto',
        right: '25px',
        background: 'var(--mantine-color-red-6)',
        bottom: '-5px',
        ...handleStyle,
      }}
    />
  </>
));

const StatStartNode = memo(({ data }) => (
  <>
    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      <FlowStepIcon icon={data.custom?.key} />
      <Text size="sm" component="span">
        {nodeLabel(data)}
      </Text>
    </Box>
    <Handle type="source" position="bottom" isConnectable={false} style={{ bottom: '-5px', ...handleStyle }} />
  </>
));

const nodeTypes = {
  default: StatDefaultNode,
  conditional: StatBranchNode,
  abSplit: StatBranchNode,
  start: StatStartNode,
};

const FlowStepStatsGraph = ({ campaign, stepsById, currency }) => {
  const { nodes, edges } = useMemo(() => {
    const rawNodes = campaign?.flow?.nodes || [];
    const rawEdges = campaign?.flow?.edges || [];
    return {
      nodes: rawNodes.map(({ width, height, measured, ...node }) => ({
        ...node,
        data: { ...node.data, step: stepsById.get(String(node.id)), currency },
        draggable: false,
        connectable: false,
        selectable: false,
      })),
      edges: rawEdges.map((edge) => ({ ...edge, selectable: false, focusable: false })),
    };
  }, [campaign?.flow?.nodes, campaign?.flow?.edges, stepsById, currency]);

  return (
    <Box style={{ height: 600 }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.2}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          edgesFocusable={false}
          deleteKeyCode={null}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </Box>
  );
};

export default FlowStepStatsGraph;
